import type { RefObject } from 'react';
import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { adaptReviewToUI } from 'src/adapters/storeToUI/adaptReviewToUI';
import { trackReviewCardControlClick } from 'src/components/organisms/CustomersReviewsPreview/tracking/customersReviewsPreviewEvents';
import { getReviewsDrawerHref } from 'src/fragmentIDs';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectProductPartner } from 'src/redux/product/selectors';
import {
  selectRatingBreakdown,
  selectReviewsList,
  selectReviewsTotalCount,
} from 'src/redux/reviews/selectors';
import { actions as reviewsActions } from 'src/redux/reviews/slice';
import { requestReviewsPage } from 'src/redux/reviews/thunks';
import type { AppThunkDispatch } from 'src/redux/store';
import type { CustomersReviewsDrawerProps } from '../components/CustomersReviewsDrawer';
import { CustomersReviewsDrawer } from '../components/CustomersReviewsDrawer';
import { useReviewsDrawerFragment } from '../hooks/useReviewsDrawerFragment';
import {
  trackDrawerOpen,
  trackNextPageClick,
  trackPaginationClick,
  trackPreviousPageClick,
  trackRatingSystemInfoClick,
  trackSharedReviewOpen,
} from '../tracking/customersReviewsDrawerEvents';

/**
 * Wires the reviews drawer to the store, the URL and tracking, so the drawer stays pure
 * props. The first page arrives with the page's own reviews fetch, which also carries the
 * summary, so only further pages are requested from here, once a customer asks for them.
 */
export interface CustomersReviewsDrawerContainerProps {
  /** The buy-box column the panel aligns to. */
  target: RefObject<HTMLElement | null>;
}

export const CustomersReviewsDrawerContainer = ({
  target,
}: CustomersReviewsDrawerContainerProps) => {
  const dispatch = useDispatch() as AppThunkDispatch;
  const { clearReviewId, close, isOpen, reviewId } = useReviewsDrawerFragment();
  const list = useSelector(selectReviewsList);
  const totalCount = useSelector(selectReviewsTotalCount);
  const ratingBreakdown = useSelector(selectRatingBreakdown);
  const partner = useSelector(selectProductPartner);
  const productCode = useSelector((state: ReduxApplicationState) => state.product.code);

  // The page asked for, not the one that last arrived, so a click changes the screen at once.
  const page = list.visiblePage;
  const storeReviews = list.pages[page];

  // Once per opening, not on every render or page change.
  const hasTrackedOpen = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      hasTrackedOpen.current = false;

      return;
    }

    if (!hasTrackedOpen.current) {
      hasTrackedOpen.current = true;
      trackDrawerOpen();

      if (reviewId) {
        trackSharedReviewOpen(reviewId);
      }
    }
  }, [isOpen, reviewId]);

  const reviews = useMemo(
    () => storeReviews?.map((review) => adaptReviewToUI(review, partner)) ?? null,
    [partner, storeReviews],
  );

  // Only shown when it is on the loaded page: Feefo cannot fetch one review by id, and paging
  // blind for it would be unbounded, so a link to an older review opens at the top of the list.
  const linkedReview = useMemo(
    () => (reviewId && reviews?.find(({ id }) => id === reviewId)) || null,
    [reviewId, reviews],
  );

  // A review link points at the loaded first page (the page's preview cards show page 1), so
  // a drawer left on a later page goes back to it rather than opening without its review.
  // Judged once the visible page has loaded and only when the review is not on it, so a card
  // opened from page 3 of the list stays on page 3.
  useEffect(() => {
    if (isOpen && reviewId && storeReviews && !linkedReview && page !== 1 && list.pages[1]) {
      dispatch(reviewsActions.showPage(1));
    }
  }, [dispatch, isOpen, linkedReview, list.pages, page, reviewId, storeReviews]);

  const goToPage = (nextPage: number) => {
    // The URL stops claiming a review that is no longer on screen.
    if (reviewId) {
      clearReviewId();
    }

    if (list.pages[nextPage]) {
      dispatch(reviewsActions.showPage(nextPage));

      return;
    }

    if (productCode) {
      dispatch(requestReviewsPage({ page: nextPage, productCode }));
    }
  };

  // The same event as the preview cards, so both surfaces report under one name.
  const handleControlClick: NonNullable<CustomersReviewsDrawerProps['onControlClick']> = (
    _e,
    { control },
  ) => trackReviewCardControlClick(control);

  const handlePageChange = (nextPage: number, trigger: 'next' | 'page' | 'previous') => {
    goToPage(nextPage);

    switch (trigger) {
      case 'next':
        trackNextPageClick(nextPage);
        break;
      case 'previous':
        trackPreviousPageClick(nextPage);
        break;
      default:
        trackPaginationClick(nextPage);
    }
  };

  return (
    <CustomersReviewsDrawer
      error={list.error}
      hrefForReview={getReviewsDrawerHref}
      isOpen={isOpen}
      linkedReview={linkedReview}
      onBack={clearReviewId}
      onClose={close}
      onControlClick={handleControlClick}
      onPageChange={handlePageChange}
      onRatingSystemInfoClick={trackRatingSystemInfoClick}
      page={page}
      ratingBreakdown={ratingBreakdown ?? null}
      reviews={reviews}
      target={target}
      totalCount={totalCount}
      totalPages={list.meta?.totalPages ?? 1}
    />
  );
};
